import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-quotation-login',
  templateUrl: './quotation-login.component.html',
  styleUrls: ['./quotation-login.component.scss']
})
export class QuotationLoginComponent implements OnInit {
  loginForm: FormGroup;
  errorMessage: string | null = null;
  isLoading = false;
  showPassword = false;
  rememberMe = false;
  returnUrl: string = '/quotation-part/quotation-dashboard';

  // Success Toast
  showSuccessToast = false;
  successMessage = '';
  successSubtitle = '';
  successType: 'login' | 'reset' = 'login';

  // Forgot Password
  forgotPasswordMode = false;
  resetEmployeeNumber = '';
  showOtpStep = false;
  otpCode = '';
  newPassword = '';
  otpError = '';
  employeeError = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.loginForm = this.fb.group({
      employeeNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{1,10}$/)]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit(): void {
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/quotation-part/quotation-dashboard']);
      return;
    }

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/quotation-part/quotation-dashboard';
    this.loadRememberedCredentials();
    this.setupRealTimeValidation();
  }

  private loadRememberedCredentials(): void {
    const rememberedEmployee = localStorage.getItem('rememberedEmployee');
    if (rememberedEmployee) {
      this.loginForm.patchValue({ employeeNumber: rememberedEmployee });
      this.rememberMe = true;
    }
  }

  private setupRealTimeValidation(): void {
    this.loginForm.valueChanges.subscribe(() => {
      if (this.errorMessage) {
        this.errorMessage = null;
      }
    });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  onForgotPassword(event: Event) {
    event.preventDefault();
    this.forgotPasswordMode = true;
    this.showOtpStep = false;

    // Clear all values
    this.resetEmployeeNumber = '';
    this.otpCode = '';
    this.newPassword = '';
    this.otpError = '';
    this.employeeError = '';

    // Force clear browser autofill after a short delay
    setTimeout(() => {
      const resetEmpInput = document.getElementById('resetEmployeeNumber') as HTMLInputElement;
      const otpInput = document.getElementById('resetOtp') as HTMLInputElement;
      const newPwInput = document.getElementById('newPassword') as HTMLInputElement;

      if (resetEmpInput) resetEmpInput.value = '';
      if (otpInput) otpInput.value = '';
      if (newPwInput) newPwInput.value = '';
    }, 50);
  }

  onBackToLogin() {
    this.forgotPasswordMode = false;
    this.showOtpStep = false;
    this.resetEmployeeNumber = '';
    this.otpCode = '';
    this.newPassword = '';
    this.otpError = this.employeeError = '';
  }

  async sendResetEmail() {
    if (!this.resetEmployeeNumber || this.isLoading) return;

    this.isLoading = true;
    this.employeeError = '';

    try {
      await this.authService.forgotPassword(this.resetEmployeeNumber).toPromise();
      this.showOtpStep = true;

      // Clear OTP and password fields
      this.otpCode = '';
      this.newPassword = '';
      this.otpError = '';

      // Force clear autofill
      setTimeout(() => {
        const otpInput = document.getElementById('resetOtp') as HTMLInputElement;
        const newPwInput = document.getElementById('newPassword') as HTMLInputElement;

        if (otpInput) {
          otpInput.value = '';
          otpInput.focus();
        }
        if (newPwInput) newPwInput.value = '';
      }, 100);

      // Show success toast
      this.displaySuccessToast(
        'Verification Code Sent!',
        'Check your email for the 6-digit code',
        'reset'
      );
    } catch (error: any) {
      this.employeeError = error.error?.message || 'Failed to send code';
    } finally {
      this.isLoading = false;
    }
  }

  async resetPassword() {
    if (this.otpCode.length !== 6 || this.newPassword.length < 6 || this.isLoading) return;

    this.isLoading = true;
    this.otpError = '';

    try {
      await this.authService.resetPassword(this.resetEmployeeNumber, this.otpCode, this.newPassword).toPromise();

      // Show success toast
      this.displaySuccessToast(
        'Password Reset Successful!',
        'You can now sign in with your new password',
        'reset'
      );

      // Close modal after delay
      setTimeout(() => {
        this.onBackToLogin();
      }, 2000);

    } catch (error: any) {
      this.otpError = error.error?.message || 'Invalid code or password too weak';
    } finally {
      this.isLoading = false;
    }
  }

  onSubmit(): void {
    this.markFormGroupTouched(this.loginForm);

    if (this.loginForm.invalid) {
      this.errorMessage = 'Please fill all fields correctly.';
      this.shakeForm();
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    const { employeeNumber, password } = this.loginForm.value;

    if (this.rememberMe) {
      localStorage.setItem('rememberedEmployee', employeeNumber);
    } else {
      localStorage.removeItem('rememberedEmployee');
    }

    this.authService.login(employeeNumber, password).subscribe({
      next: (response) => {
        this.authService.setCurrentUser(response.user);

        // Show success toast
        this.displaySuccessToast(
          'Login Successful!',
          `Welcome back, ${response.user.name || 'User'}`,
          'login'
        );

        // Navigate after delay
        setTimeout(() => {
          this.router.navigate([this.returnUrl]);
        }, 1500);
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = 'Invalid credentials. Please try again.';
        this.shakeForm();
      }
    });
  }

  private displaySuccessToast(message: string, subtitle: string, type: 'login' | 'reset'): void {
    this.successMessage = message;
    this.successSubtitle = subtitle;
    this.successType = type;
    this.showSuccessToast = true;

    // Auto-hide after 4 seconds
    setTimeout(() => {
      this.closeToast();
    }, 4000);
  }

  closeToast(): void {
    this.showSuccessToast = false;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  private shakeForm(): void {
    const formElement = document.querySelector('.form-container');
    if (formElement) {
      formElement.classList.add('shake');
      setTimeout(() => formElement.classList.remove('shake'), 500);
    }
  }

  // Template getters
  get employeeNumber() { return this.loginForm.get('employeeNumber'); }
  get password() { return this.loginForm.get('password'); }
}
